import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export interface IdleWarningDialogProps {
  open: boolean;
  /** Total seconds the user has to respond. Restarts each time the dialog opens. */
  countdownSeconds: number;
  onStaySignedIn: () => void;
  onSignOutNow: () => void;
}

const IdleWarningDialog: React.FC<IdleWarningDialogProps> = ({
  open,
  countdownSeconds,
  onStaySignedIn,
  onSignOutNow,
}) => {
  const [remaining, setRemaining] = useState(countdownSeconds);

  useEffect(() => {
    if (!open) return;
    setRemaining(countdownSeconds);
    const interval = window.setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [open, countdownSeconds]);

  const progress = countdownSeconds > 0
    ? Math.max(0, Math.min(100, (remaining / countdownSeconds) * 100))
    : 0;

  return (
    <Dialog
      open={open}
      onClose={(_evt, reason) => {
        // Don't allow casual dismiss — user must make an explicit choice.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onStaySignedIn();
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="idle-warning-title"
    >
      <DialogTitle id="idle-warning-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTimeIcon color="warning" />
        Session about to expire
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You've been inactive for a while. For your security, you'll be signed out
          automatically in <strong>{remaining}</strong> second{remaining === 1 ? '' : 's'}.
        </Typography>
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={remaining <= 15 ? 'error' : 'warning'}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Click <strong>Stay signed in</strong> to continue your session.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSignOutNow} color="inherit">
          Sign out now
        </Button>
        <Button onClick={onStaySignedIn} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleWarningDialog;
